import { z } from "zod";
import { compareWorkOrderPriority } from "../availability";
import { ACTIVE_WORK_ORDER_STATUSES, type WorkOrderStatus } from "../enums";
import { id } from "../schemas/common";
import { labelCodeInput } from "./codes";

/*
 * Taking parts with the scanner (ADR 0018 › The scanner): a Take comes off stock
 * at once, booked to a work order or to none. On a work order it counts against the part's line;
 * a part the work order doesn't list gets a line of its own. Completing the work order then books
 * only what was used beyond what was taken.
 */

/** The work orders a Take can be booked to: planned or in progress. */
export function takesBookable(status: WorkOrderStatus): boolean {
  return (ACTIVE_WORK_ORDER_STATUSES as readonly WorkOrderStatus[]).includes(status);
}

/**
 * Which line of the work order a Take counts against: the part's first line that uses up stock;
 * a line of its own when the work order doesn't list the part; none when it lists it only as a
 * tool, which isn't used up.
 */
export function takeLine(
  lines: readonly { id: string; consumesStock: boolean }[],
): { kind: "line"; id: string } | { kind: "add" } | { kind: "none" } {
  const consuming = lines.find((line) => line.consumesStock);
  if (consuming) return { kind: "line", id: consuming.id };
  return lines.length === 0 ? { kind: "add" } : { kind: "none" };
}

/** What a line still claims of the stock once `taken` of it came off by scanning. */
export function stillRequired(qty: number, taken: number): number {
  return Math.max(0, qty - taken);
}

/** The used amount completing a work order suggests: the planned one, or what was taken if more. */
export function suggestedQtyUsed(qty: number, taken: number): number {
  return Math.max(qty, taken);
}

/** A work order the picking round's question offers. */
export interface TakeWorkOrder {
  id: string;
  status: WorkOrderStatus;
  plannedOn: string | null;
  createdAt: string;
  /** It has a line for the part just scanned. */
  listsPart: boolean;
}

/**
 * The work orders the first Take of a picking round offers: only those that take bookings, the
 * ones that list the scanned part first, then in the order stock goes to them (in progress, then
 * by planned date).
 */
export function takeWorkOrders<T extends TakeWorkOrder>(workOrders: readonly T[]): T[] {
  return workOrders
    .filter((w) => takesBookable(w.status))
    .sort((a, b) => Number(b.listsPart) - Number(a.listsPart) || compareWorkOrderPriority(a, b));
}

/**
 * The stock change completion books for a line: what was used beyond what was taken comes off
 * (negative); what was taken but not used goes back on (positive).
 */
export function completionDelta(qtyUsed: number, taken: number): number {
  return taken - qtyUsed;
}

const amount = z
  .int({ error: "validation.number" })
  .positive({ error: "validation.positive" })
  .max(1_000_000_000, { error: "validation.tooLarge" });

/** Take or Add on a scanned label; a Take may be booked to a work order. */
export const labelTakeInput = z.object({
  code: labelCodeInput,
  qty: amount,
  workOrderId: id.nullish().transform((v) => v ?? null),
});
export const labelAddInput = z.object({ code: labelCodeInput, qty: amount });
/** Correct now: the amount counted on the shelf. */
export const labelCorrectInput = z.object({
  code: labelCodeInput,
  qty: z
    .int({ error: "validation.number" })
    .min(0, { error: "validation.positive" })
    .max(1_000_000_000, { error: "validation.tooLarge" }),
});
export const labelUndoInput = z.object({ code: labelCodeInput, movementId: id });
/** The round's question; with the scanned label, its part's work orders come first. */
export const labelWorkOrdersInput = z.object({
  code: labelCodeInput.nullish().transform((v) => v ?? null),
});
